// Single source of truth for all German copy of the SVS year-end check V2
// prototypes. All three variants (classic / flow / scroll) render from this
// file — they may shorten, reorder or split it across screens, but must not
// invent new tax or SVS statements. Figures are deliberately rounded: this is
// a rough, simplified explanation for existing clients, not an SVS course.

export type Detail = { title: string; body: string[] };
export type Point = { title: string; text: string; detail?: Detail };

export const MIN_QUARTER = 482.43; // Mindestbeitrag pro Quartal 2026 (160,81 €/Monat inkl. UV, wko.at)
export const MIN_YEAR = 1929.72; // 4 × 482,43 €
export const MIN_NOTE = 'entspricht den Mindestbeiträgen';
export const MAX_NOTE = 'Höchstbeitrag';
export const THRESHOLD = 1000; // ab dieser offenen Summe lohnt sich die Vorauszahlung

// The process steps, used for the always-visible progress indicator.
export const PROGRESS_STEPS = [
  { id: 'warum', label: 'Warum' },
  { id: 'betrifft', label: 'Betrifft es mich?' },
  { id: 'ablauf', label: 'So läuft’s' },
  { id: 'rechner', label: 'Rechner' },
  { id: 'todo', label: 'Was du tust' },
] as const;

export const hero = {
  eyebrow: 'SVS-Check 2026',
  title: 'Freiwillige SVS-Vorauszahlung',
  lead:
    'Wer gut verdient, aber nur den Mindestbeitrag zahlt, bekommt später eine große Nachzahlung. Wir schauen uns das mit dir vor dem 31. Dezember an – damit du weißt, was kommt.',
  readingTime: 'ca. 2 Minuten',
  yourTask: 'Deine Aufgabe jetzt: Buchhaltung auf Stand bringen. Wir melden uns ab 10. November.',
};

// "Warum" – the basic SVS logic, three-year timeline.
export const why = {
  title: 'Warum das Thema überhaupt?',
  intro:
    'Die SVS weiß während des Jahres nicht, was du verdienst. Sie verrechnet vorläufig – oft nur den Mindestbeitrag. Die echte Rechnung kommt erst Jahre später.',
  timeline: [
    {
      year: '2026',
      label: 'Vorläufige Beiträge',
      text: 'Die SVS schreibt dir vorläufig nur den Mindestbeitrag vor: 482,43 € pro Quartal.',
    },
    {
      year: '2027',
      label: 'Steuerbescheid',
      text: 'Dein Einkommensteuerbescheid 2026 kommt. Erst jetzt weiß die SVS, was du wirklich verdient hast.',
    },
    {
      year: '2028',
      label: 'Nachzahlung',
      text: 'Die SVS schreibt die Differenz für 2026 nach – oft mehrere tausend Euro, verteilt auf 4 Quartale.',
    },
  ],
  punchline: 'Das Geld schuldest du so oder so. Die Frage ist nur: Überrascht es dich – oder planst du es ein?',
  ruleOfThumb: {
    title: 'Faustregel',
    text: 'Rechne mit rund 20 % deines Umsatzes für die SVS.',
    detail: {
      title: 'Woher kommen die 20 %?',
      body: [
        'Nach Pauschalierung und Gewinnfreibetrag bleiben von deinem Umsatz grob 70 % als Gewinn übrig.',
        'Davon gehen rund 27 % an die SVS (Pension, Kranken, Selbständigenvorsorge). 70 % × 27 % ≈ 20 % vom Umsatz.',
        'Das ist bewusst grob. Deine echte Zahl rechnen wir im November mit deinen Daten.',
      ],
    },
  },
};

// "Betrifft es mich?"
export const concern = {
  title: 'Betrifft dich das?',
  intro: 'Wahrscheinlich ja, wenn beides zutrifft:',
  checks: [
    'Du zahlst aktuell nur den Mindestbeitrag (482,43 € pro Quartal).',
    'Dein Umsatz liegt heuer deutlich über ca. 10.000 €.',
  ],
  yes: {
    title: 'Besonders sinnvoll, wenn …',
    points: [
      'du dein Business beendest oder pausierst,',
      'du nächstes Jahr deutlich weniger verdienen wirst – z. B. Auslandsaufenthalt, Karenz, ein absehbar schwächeres Jahr,',
      'du heuer gut verdienst und die Nachzahlung nicht erst in zwei Jahren spüren willst.',
    ],
  },
  no: {
    title: 'Eher nicht nötig, wenn …',
    points: [
      'du gerade erst gestartet bist oder neu in Österreich bist,',
      'dein Umsatz niedrig ist (z. B. rund 10.000 €),',
      'dein Gewinn heuer ohnehin niedrig ist, weil du hohe SVS-Nachzahlungen für Vorjahre zahlst – dann lieber abwarten.',
    ],
  },
  careful: {
    title: 'Achtung bei großen Beträgen',
    text: 'Zahl nicht mehr vor, als du dir leisten kannst – besonders, wenn nächstes Jahr wenig oder kein Einkommen kommt.',
    detail: {
      title: 'Warum?',
      body: [
        'Wer z. B. 20.000 € vorauszahlt und im Folgejahr kaum Einkommen hat, kann in einen Engpass rutschen.',
        'Die Vorauszahlung ist freiwillig. Wir empfehlen sie nur, wenn sie zu deiner Situation passt.',
      ],
    },
  },
  notForMe: {
    title: 'Trifft auf dich (noch) nicht zu?',
    text: 'Dann musst du heuer nichts tun. Lies trotzdem gern weiter – spätestens wenn dein Umsatz wächst, wird das Thema relevant.',
  },
};

// "So läuft's" – process timeline for clients.
export const process = {
  title: 'So läuft der Check ab',
  intro: 'Du musst dir keine Frist merken. Wir melden uns bei dir.',
  steps: [
    {
      when: 'Jetzt',
      title: 'Buchhaltung auf Stand bringen',
      text: 'Buch alles mindestens bis September, am besten bis Oktober. SVS-Daten brauchen wir von dir keine.',
      you: true,
    },
    {
      when: 'Ab 10. November',
      title: 'Wir melden uns',
      text: 'Sobald die SVS die Q4-Vorschreibungen online stellt, holen wir uns deine SVS-Daten automatisch und rechnen eine Schätzung.',
      detail: {
        title: 'Warum nicht schon im September?',
        body: [
          'Im September fehlen bei den meisten noch die Buchhaltungsdaten. Eine Schätzung ohne Zahlen bringt nichts.',
          'Ab Mitte November ist das Jahr gut lesbar – und die SVS-Daten sind vollständig.',
        ],
      },
    },
    {
      when: 'Bis 15. Dezember',
      title: 'Nichts gehört?',
      text: 'Meistens heißt das: Bei dir lohnt es sich heuer nicht. Wenn du glaubst, dass es dich betrifft, melde dich bitte bei uns.',
      you: true,
    },
    {
      when: 'Bis ca. 27. Dezember',
      title: 'Einzahlen',
      text: 'Wenn es sich lohnt, bekommst du von uns die Zahlungsinfos: IBAN der SVS, deine Sozialversicherungsnummer, Betrag. Überweis rechtzeitig, damit das Geld noch heuer bei der SVS ist.',
      you: true,
    },
    {
      when: 'Ab Februar 2027',
      title: 'Endabrechnung (freiwillig)',
      text: 'Sobald dein Steuerbescheid 2026 da ist, fragen wir dich, ob wir die SVS um eine vorzeitige Endabrechnung bitten sollen. Dann ist 2026 oft schon bis Mitte 2027 abgeschlossen – statt erst 2028.',
    },
  ],
};

// Calculator copy
export const calculator = {
  title: 'Grob überschlagen: Was kommt auf dich zu?',
  intro: 'Schieb die Regler. Das ist eine vereinfachte Schätzung – die echte Zahl rechnen wir im November mit deinen Daten.',
  modeLabel: 'Rechnen mit',
  modes: {
    revenue: { label: 'Umsatz', hint: 'Einfach: SVS ≈ 20 % vom Umsatz' },
    profit: { label: 'Gewinn', hint: 'Genauer: SVS ≈ 26,83 % vom Gewinn' },
  },
  incomeLabel: {
    revenue: 'Dein voraussichtlicher Umsatz 2026',
    profit: 'Dein voraussichtlicher Gewinn 2026',
  },
  svsLabel: 'SVS-Beiträge für 2026 (geschätzt)',
  paidLabel: 'Bisher für 2026 bezahlte SVS-Beiträge',
  paidHint: 'Standard: Mindestbeitrag, 4 × 482,43 € = 1.929,72 €',
  paidWarning: {
    text: 'Nur Beiträge für 2026 zählen. Nicht alles, was du heuer an die SVS überwiesen hast, zählt auch als Vorauszahlung für das Jahr 2026.',
    detail: {
      title: 'Warum ist das wichtig?',
      body: [
        'Was du heuer an die SVS gezahlt hast, kann auch Nachzahlungen aus Vorjahren enthalten. Diese Nachzahlungen wirken nicht als Vorauszahlung für das laufende Jahr.',
        'Beispiel: Du zahlst heuer 10.000 € an die SVS. Davon sind 8.000 € eine Nachzahlung für Vorjahre – dann wirken nur 2.000 € als Vorauszahlung für 2026.',
        'Die genaue Aufteilung rechnen wir im November für dich aus – mit deinen aktuellen Daten aus dem SVS-Portal.',
      ],
    },
  },
  resultLabel: 'Geschätzte Nachzahlung 2026',
  results: {
    none: {
      title: 'Keine Vorauszahlung nötig',
      text: 'Nach dieser groben Schätzung zahlst du für heuer schon genug SVS-Beiträge. Wir sehen keinen dringenden Handlungsbedarf.',
    },
    small: {
      title: 'Vorauszahlung nicht dringend',
      text: 'Unter ca. 1.000 € sehen wir eine Vorauszahlung als nicht unbedingt nötig, da die Schätzung auch abweichen kann.',
    },
    lowRate: {
      title: 'Vorauszahlung nicht dringend',
      text: 'Es wird eine Nachzahlung kommen. Eine Vorauszahlung ist aber nicht dringend nötig, weil dein Steuersatz heuer nicht besonders hoch ist. Leg dir das Geld für die Nachzahlung trotzdem zur Seite.',
    },
    relevant: {
      title: 'Vorauszahlung sinnvoll',
      text: 'Es wird vermutlich zu einer Nachzahlung von SVS-Beiträgen kommen, sodass eine freiwillige Vorauszahlung Sinn machen kann. Wir melden uns ab ca. 10. November mit einer genaueren Schätzung.',
    },
  },
  assumptions: {
    title: 'Annahmen',
    body: [
      'Mindestbeitrag 2026: 482,43 € pro Quartal (1.929,72 € pro Jahr).',
      'Umsatz-Variante: SVS ≈ 20 % vom Umsatz. Gewinn-Variante: SVS ≈ 26,83 % vom Gewinn.',
      'Höchstens ca. 26.200 € pro Jahr (Höchstbeitragsgrundlage 2026: 97.020 €).',
      'Der Rechner beginnt bei 10.000 € Umsatz bzw. 6.500 € Gewinn. Darunter macht eine Vorauszahlung vermutlich keinen Sinn; Pflichtbeiträge fallen in der Regel erst ab ca. 6.613 € Gewinn an.',
      'Wenn du erst während des Jahres begonnen (oder aufgehört) hast, berücksichtigt der Rechner das nicht korrekt.',
      'Vereinfacht, ohne Sonderregeln für Neugründer und Hinzurechnungen. Gerundet.',
      'Das ist nur eine grobe Schätzung.',
    ],
  },
  disclaimer: 'Grobe, vereinfachte Schätzung. Keine Beratung, keine verbindliche Zahl.',
};

// "Keep paying" – one of the most important messages.
export const keepPaying = {
  title: 'Wichtig: Zahl die Vorschreibungen weiter!',
  text: 'Deine Vorauszahlung liegt als Guthaben auf deinem SVS-Konto. Die SVS nimmt davon automatisch die nächsten Quartale – wenn du nicht weiterzahlst, ist das Guthaben weg, bevor die Nachzahlung kommt.',
  detail: {
    title: 'Was genau passiert mit dem Guthaben?',
    body: [
      'Wir erhöhen deine Beitragsgrundlage bewusst nicht. Du zahlst freiwillig ein – so wird nichts sofort nachverrechnet.',
      'Die SVS verbucht das Guthaben zuerst gegen die laufenden Vorschreibungen (Q1, Q2 …) und spätestens mit der Endabrechnung gegen die Nachzahlung für 2026.',
      'Letztes Jahr hatten einige ein Guthaben von z. B. 10.000 € – die Quartale danach wurden davon abgebucht, und als die Nachzahlung kam, war nichts mehr da.',
      'Darum: Jede neue Vorschreibung ganz normal weiterzahlen.',
    ],
  },
};

// Honest framing – it's a shift, not a saving.
export const honest = {
  title: 'Ehrlich gesagt',
  text: 'Die Vorauszahlung spart dir keine SVS – sie verschiebt nur den Zeitpunkt. Der Hauptnutzen: Du weißt, was kommt, und wirst nicht überrascht.',
  points: [
    {
      title: 'Steuerlich meist besser als ein Last-Minute-Kauf',
      text: 'Statt im Dezember noch schnell einen Laptop zu kaufen: Die SVS-Zahlung senkt deinen Gewinn 2026 voll.',
      detail: {
        title: 'Beispiel',
        body: [
          'Ein Laptop um 4.000 € im Dezember: Halbjahresabschreibung und Privatanteil – absetzbar sind heuer vielleicht nur rund 500 €.',
          'Dieselben 4.000 € an die SVS: zählen heuer voll als Ausgabe. Außer natürlich, du brauchst den Laptop wirklich.',
        ],
      },
    },
    {
      title: 'Es gleicht sich später aus',
      text: 'SVS-Beiträge werden im Jahr ihrer Vorschreibung wieder zur Beitragsgrundlage hinzugerechnet.',
      detail: {
        title: 'Was heißt das?',
        body: [
          'Spürbar ist das nur bei sehr hohen Beiträgen (10.000–20.000 €) – und es wäre ohnehin gekommen.',
          'Die Details rechnen wir für dich. Du musst dafür nichts tun.',
        ],
      },
    },
  ] as Point[],
};

// What we need from you
export const todo = {
  title: 'Was du jetzt tun kannst',
  items: [
    { title: 'Buchhaltung auf Stand bringen', text: 'Mindestens bis September, am besten bis Oktober.' },
    { title: 'Große Änderungen mitdenken', text: 'Eine große Rechnung im Dezember, ein Auftrag, der wegfällt? Sag es uns, wenn wir uns melden.' },
    { title: 'Auf unsere Nachricht antworten', text: 'Ab 10. November. Ein kurzes Hin und Her reicht.' },
    { title: 'Vorschreibungen weiterzahlen', text: 'Auch wenn du ein Guthaben hast.' },
  ],
  weHandle: 'Den Rest übernehmen wir: SVS-Daten abrufen, schätzen, Zahlungsinfos schicken, Endabrechnung beantragen.',
};

export const faq: { q: string; a: string }[] = [
  {
    q: 'Muss ich das machen?',
    a: 'Nein. Alles ist freiwillig. Wir sagen dir nur rechtzeitig, was auf dich zukommt.',
  },
  {
    q: 'Was, wenn wir zu hoch schätzen?',
    a: 'Dann bleibt ein Guthaben auf deinem SVS-Konto und wird mit späteren Beiträgen verrechnet. Es geht nicht verloren. Eine Auszahlung gilt steuerlich als Betriebseinnahme – wir raten davon ab.',
  },
  {
    q: 'Ich habe gerade gegründet – gilt das für mich?',
    a: 'In den ersten Jahren gelten bei der SVS Sonderregeln. Die berücksichtigen wir in unserer Berechnung – du musst nichts extra tun.',
  },
  {
    q: 'Wie genau ist die Schätzung?',
    a: 'Es handelt sich um eine grobe Vorberechnung. Aus unserer Erfahrung macht eine genaue Berechnung zu diesem Zeitpunkt keinen Sinn, da sich bei Umsatz, Ausgaben und anderen Faktoren bis Jahresende noch einiges ändern kann.',
  },
];

export const cta = {
  button: 'Verstanden!',
  statement:
    'Ich bringe meine Buchhaltung auf Stand und sage PSTax meinen voraussichtlichen Gewinn, sobald ihr euch im November meldet.',
  confirm: {
    title: 'Danke – wir melden uns ab 10. November.',
    summary: [
      'Jetzt: Buchhaltung bis September/Oktober auf Stand bringen.',
      'Ab 10. November: Wir schicken dir unsere Schätzung.',
      'Bis 15. Dezember nichts gehört und du glaubst, es betrifft dich? Melde dich.',
      'Nach dem Einzahlen: Vorschreibungen ganz normal weiterzahlen.',
    ],
    contact: 'Fragen? Schreib uns einfach – wir sind für dich da.',
  },
  privacyNote: 'Wir speichern, dass du diese Info bestätigt hast, damit wir wissen, wen wir im November kontaktieren.',
};

export const footerNote =
  'Stand: September 2026. Vereinfachte, allgemeine Information für Klient:innen von PSTax – keine individuelle Beratung. Geprüft von Pascal Schraml, Steuerberater, PSTax Steuerberatung FlexCo.';
